import { StringUtils } from '@ispm/shared/utils/String.utils';
import {ChangeDetectionStrategy, ChangeDetectorRef,Component, Inject, Input, OnInit} from "@angular/core";
import {Widget} from "@ispm/widgets/decorator/widget.decorator";
import {IWidgetItem} from "@ispm/widgets/interfaces/widget-item.interface";
import {IdynamicFormItem} from "@ispm/dynamic-form/interfaces/IdynamicFormItem";
import {IspmHttpClient} from "@ispm/core/services/ispm-http-client";
import { setSetLogiDashboardsDetails } from '../store/actions/analytics.actions';
import { Store } from '@ngrx/store';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { logiDashboardsDetailsSelector } from '../store/selectors/analytics.selector';
import {map, share, take} from "rxjs/operators";
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';

@Component({
  selector: 'logi-embed-visual',
  templateUrl: './logi-embed-visual.component.html',
  styleUrls: ['./logi-embed-visual.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@Widget('LogiEmbedVisual')
export class LogiEmbedVisualComponent implements OnInit, IWidgetItem {

  // generate unique UUID for the visual's container
  private uuid: string = StringUtils.generateGuid();
  public sectionUUID: string = this.uuid + '-embedSection';
  public containerUUID: string = this.uuid + '-embedVisual';

  @Input() public dashboardId: string;
  @Input() public dashboardName: string;
  @Input() public visualTheme: string; //= "ispm";
  @Input() public canEditVisual;// = false;
  @Input() public visualType;//='dashboard';
  @Input() public interactivityOverrides;
  @Input() public header;
  @Input() public mode;


  set widgetConfig(_itemConfig: IdynamicFormItem) {
    const dashboardProperties = _itemConfig.widgetPayload.dashboard.properties;
    this.dashboardId = dashboardProperties.dashboardId;
    this.dashboardName = dashboardProperties.dashboardName;
    this.visualTheme = dashboardProperties.theme;
    this.canEditVisual = dashboardProperties.edit;
    this.visualType = dashboardProperties.type;
    this.interactivityOverrides = dashboardProperties.interactivityOverrides;
    this.header = dashboardProperties.header;
    this.mode = dashboardProperties.mode;
  }

  set widgetData(widgetData: any) {

  }
  //====================================================================================================================

  constructor(private ispmHttpClient: IspmHttpClient,
              private notificationMessageService: NotificationMessageService,
              private store: Store<IgenomicsState>,
              private detector?: ChangeDetectorRef) {
  }
  //====================================================================================================================

  /**
   * IMPORTANT: the sample code bellow is based on the embed.js file & the examples that are provided as part of the Logi
   *            Composer trial install package!!!!!
   */
  ngOnInit() {
    // add a window.logiGetToken function to obtain an access token and expiration for the current user
    // @ts-ignore
    if (! window.composerGetToken) {

      // @ts-ignore
      window.composerGetToken = () => this.getToken();
    }
    //-----------------------------------------------------------------------------------------------------------

    // add a window.logiEmbedObject to hold various data that is required to embed a dashboard
    // @ts-ignore
    if (! window.logiEmbedObject) {

      // @ts-ignore
      window.logiEmbedObject = {
        addViz: this.addViz, // add JavaScript function to call the internal addViz() function
        manager: null, // a reference to the LogiEmbedManager instance
        embedDataList: [], // an array of all dashboards that are awaiting to be written
        loaded: false // indicate if embed script was fully loaded
      };

    }
    //-----------------------------------------------------------------------------------------------------------

  }
  //====================================================================================================================


  private getLogiEndpoint() {
    // console.log("Call to getLogiEndpoint() function");
    return this.ispmHttpClient.get('analytics/logi/composer/endpoint')
      .pipe(
        share(),
        take(1),
        map(endpoint => endpoint)
      ).toPromise();
  }

  private getLogiDashboards() {
    return this.ispmHttpClient.get('analytics/logi/getLogiDashboardsDetails')
      .pipe(
        share(),
        take(1),
        map(dash => dash)
      ).toPromise();
  }

  /**
   * generate an updated access-token for the current user. Token is generated at the web-server.
   *
   * this function is called from window.logiGetToken whenever current token is expired.
   */
  private async getToken() {
    const token = await this.ispmHttpClient.post('analytics/logi/access-token', {})
      .pipe(
        share(),
        take(1)
      )
      .toPromise();
    return {access_token: token.access_token, expires_in: token.expires_in};

  }
  //====================================================================================================================

  ngAfterViewInit() {
    this.embedComposerDashboard();
  }
  //====================================================================================================================

  /**
   * construct all required scripts and trigger the dashboard to build
   */
  async embedComposerDashboard() {

    //First, gets the correct dashboardId from Logi (and later on from the Redux)
    let dashboardId: string = this.dashboardId;
    let logiDashboardsDetails: any;
    this.store.select(logiDashboardsDetailsSelector).pipe(take(1)).subscribe(dashboards => logiDashboardsDetails = dashboards);

    if (!logiDashboardsDetails){
      logiDashboardsDetails = await this.getLogiDashboards();
      if (logiDashboardsDetails.error){
        console.error(logiDashboardsDetails.error);
        this.notificationMessageService.warning('Cannot load dashboard: ' + logiDashboardsDetails.error);
        this.detector.markForCheck();  
        return;
      }
      this.store.dispatch(setSetLogiDashboardsDetails(logiDashboardsDetails));
    }

    if (Array.isArray(logiDashboardsDetails) && logiDashboardsDetails.length > 0){
      //dashboardName is the identifier (dashboardId is changed when importing a dashboard)
      let currDashboard = logiDashboardsDetails.find(item=> item.name === this.dashboardName);
      if (currDashboard){
        dashboardId = currDashboard.id;
      }
    }

    // add an object with the details of the new dashboard to load
    // Note that all dashboards will be loaded ONLY after Logi-Embed-Manager has been initialized. Until than, hold the
    // data of all dashboards to load in logiEmbedObject.embedDataList object
    // @ts-ignore
    window.logiEmbedObject.embedDataList.push(
      {
        parentId: this.sectionUUID,
        targetId: this.containerUUID,
        dashboardId: dashboardId,
        visualTheme: this.visualTheme,
        canEditVisual: this.canEditVisual,
        visualType: this.visualType,
        interactivityOverrides: this.interactivityOverrides,
        header: this.header,
        mode: this.mode
      }
    );

    // get if the 'embed.js' script is already included in the page.
    // if script is not loaded yet, load it from the Logi Composer server
    // Note: In case that there multiple dashboards to load on the same form, this script should be loaded only once for
    //       the given window.
    let embedScript: any = document.querySelector('script[data-name="composer-embed"]');

    if (!embedScript) {
      embedScript = document.createElement('script');
      const endpoint: any = await this.getLogiEndpoint();
      if (endpoint.error){
        console.error(endpoint.error);
        this.notificationMessageService.warning(endpoint.error);
        this.detector.markForCheck();  
        return;
      }
      embedScript.src = endpoint.uri + "/composer/embed/embed.js";
      embedScript.setAttribute("data-name", "composer-embed");

      // wait until script is loaded asynchronously
      embedScript.onload = () => {

        new Promise(r => {

          // call to initialize the Logi-Embed-Manager. initLogiEmbedManager method is created within the embed.js script
          // @ts-ignore
          window.initComposerEmbedManager({getToken: window.composerGetToken}).then(embedManager => {

            // hold a reference to the LogiEmbedManager instance
            // @ts-ignore
            window.logiEmbedObject.manager = embedManager;

            // indicate that embed script was fully loaded
            // @ts-ignore
            window.logiEmbedObject.loaded = true;

            // write a script element for each dashboard to load
            this.writeEmbedScript();

            setTimeout(r, 150);
          });

        });

      };

      // embedScript.innerText = "{" +
      //   "  \"type\": \"dashboard\"," +
      //   "  \"dashboardId\": \"" + dashboardId + "\"" +
      //   "}";

      // append the 'embed.js' script to the container element
      document.getElementById(this.sectionUUID).appendChild(embedScript);

      // if Logi-Embed-Manager was already loaded, the related embed-script should be written immediately
      // @ts-ignore
    } else if (window.logiEmbedObject.loaded) {
      this.writeEmbedScript();
    }
  }
  //=========================================================================================================

  /**
   * write an embed-script element for each dashboard to load
   */
  writeEmbedScript() {

    // get from the window's logiEmbedObject the list of all dashboards to load
    // @ts-ignore
    let embedDataList = window.logiEmbedObject.embedDataList;

    // add a script element for each dashboard to load
    while (embedDataList.length > 0) {
      let embedData = embedDataList.shift();

      let visualScript: any = document.createElement('script');
      visualScript.type = "text/javascript";
      visualScript.innerText = "window.logiEmbedObject.addViz('" + embedData.targetId + "', '" + embedData.dashboardId + "', '" +
                                    embedData.visualTheme + "', " + embedData.canEditVisual + ", '" + embedData.visualType + "')";

      document.getElementById(embedData.parentId).appendChild(visualScript);

    }

  }
  //=========================================================================================================

  /**
   * this method is called from the embed-script to load a specific dashboard.
   * Note that the method is operated within the JavaScript script's scope
   * @param targetId
   * @param dashboardId
   * @param visualTheme
   * @param canEditVisual
   * @param visualType
   */
  private async addViz(targetId, dashboardId, visualTheme, canEditVisual, visualType, interactivityOverrides,header,mode)  {

    // // @ts-ignore
    // if (! window.logiEmbedObject.manager) {
    //
      // await new Promise(r => {
      //
      //   // @ts-ignore
      //   window.initLogiEmbedManager({getToken: () => window.logiGetToken()}).then(embedManager => {
      //
      //     // @ts-ignore
      //     window.logiEmbedObject.manager = embedManager;
      //     setTimeout(r, 150);
      //   });
      //
      // });
    //
    // }
    //-----------------------------------------------------------------------------------------------------------

    // call logi-embed-manager to create the dashboard component
    // @ts-ignore
    const dashboard = await window.logiEmbedObject.manager.createComponent(visualType, {
        "dashboardId": dashboardId,
        "root": targetId,
        "theme": visualTheme,
        "interactivityOverrides": interactivityOverrides,
        "header": header,
        "mode": mode
      }
    );
    dashboard.render(document.getElementById(targetId), { width: "100%", height: "100%" });

    // this event is call for every visual that is contained in the given dashboard
    dashboard.addEventListener('composer-visual-loaded', e => {
      // console.log("visual loaded: " + dashboardId) ;//e.detail.visualization)
    });

    // this event is call once for every dashboard that is loaded
    dashboard.addEventListener('composer-dashboard-loaded', e => {
      // console.log("dashboard loaded: " + dashboardId) ;//e.detail.dashboard)
    });

    return dashboard;
  };
}
